/**
 * Bounded project tree for Get Project Overview (workspace + tandem).
 * Limits depth, per-directory breadth (files vs folders separately), and total tree nodes.
 */

export const PROJECT_OVERVIEW_TREE_MAX_DEPTH = 3;
export const PROJECT_OVERVIEW_MAX_NAMES_PER_DIRECTORY = 20;
/** Upper bound on nodes emitted (each directory container + each file leaf counts as one). */
export const PROJECT_OVERVIEW_MAX_TREE_NODES = 2500;

function compareEntryNames(a, b) {
  return String(a.name).localeCompare(String(b.name), undefined, {
    sensitivity: "base",
  });
}

function buildPerDirectoryTruncation(moreFolders, moreFiles) {
  if (moreFolders <= 0 && moreFiles <= 0) return null;
  const parts = [];
  if (moreFolders > 0) {
    parts.push(
      `${moreFolders} more folder${moreFolders === 1 ? "" : "s"}`
    );
  }
  if (moreFiles > 0) {
    parts.push(`${moreFiles} more file${moreFiles === 1 ? "" : "s"}`);
  }
  return {
    moreFolders,
    moreFiles,
    note: `Plus ${parts.join(" and ")} not listed.`,
  };
}

/**
 * @param {object} listDirectoryTool - Workspace ListDirectoryTool instance
 * @param {object} [options]
 * @param {number} [options.maxDepth]
 * @param {number} [options.maxNamesPerDirectory]
 * @param {number} [options.maxTreeNodes]
 * @returns {Promise<{ tree: object, budget: object }>}
 */
export async function buildProjectFileTreeForOverview(
  listDirectoryTool,
  options = {}
) {
  const maxDepth =
    options.maxDepth ?? PROJECT_OVERVIEW_TREE_MAX_DEPTH;
  const maxPerDir =
    options.maxNamesPerDirectory ?? PROJECT_OVERVIEW_MAX_NAMES_PER_DIRECTORY;
  const maxNodes =
    options.maxTreeNodes ?? PROJECT_OVERVIEW_MAX_TREE_NODES;

  const budget = {
    initial: maxNodes,
    remaining: maxNodes,
    truncatedGlobally: false,
  };

  const tree = await buildDir(
    listDirectoryTool,
    "/",
    0,
    maxDepth,
    maxPerDir,
    budget
  );

  const nodesUsed = budget.initial - budget.remaining;

  const budgetSummary = {
    initial: budget.initial,
    remaining: budget.remaining,
    nodesUsed,
    truncatedGlobally: budget.truncatedGlobally,
    limitName: "maxTreeNodes",
    limitValue: maxNodes,
    footer:
      budget.truncatedGlobally
        ? `Project tree truncated: overview node budget ${maxNodes} (${nodesUsed} nodes emitted). Use @folder references or scope the workspace to see more.`
        : null,
  };

  return { tree, budget: budgetSummary };
}

async function buildDir(
  listDirectoryTool,
  path,
  depth,
  maxDepth,
  maxPerDir,
  budget
) {
  const displayName =
    path === "/" ? "(root)" : path.split("/").filter(Boolean).pop() || path;

  if (budget.remaining <= 0) {
    budget.truncatedGlobally = true;
    return {
      type: "directory",
      path,
      name: displayName,
      omittedDueToOverviewBudget: true,
      children: [],
    };
  }

  budget.remaining -= 1;

  const res = await listDirectoryTool.listDirectory(
    path,
    false,
    false,
    false
  );

  if (!res.success) {
    return {
      type: "directory",
      path,
      name: displayName,
      error: res.message,
      children: [],
    };
  }

  const entries = [...(res.data?.entries || [])];
  const dirs = entries
    .filter((e) => e.type === "directory")
    .sort(compareEntryNames);
  const files = entries
    .filter((e) => e.type === "file")
    .sort(compareEntryNames);

  const shownDirs = dirs.slice(0, maxPerDir);
  const omittedDirs = dirs.slice(maxPerDir);
  const shownFiles = files.slice(0, maxPerDir);
  const omittedFiles = files.slice(maxPerDir);

  let truncation = buildPerDirectoryTruncation(
    omittedDirs.length,
    omittedFiles.length
  );

  const children = [];

  for (const item of shownDirs) {
    if (budget.remaining <= 0) {
      budget.truncatedGlobally = true;
      children.push({
        type: "directory",
        name: item.name,
        path: item.path,
        omittedDueToOverviewBudget: true,
      });
      continue;
    }
    if (depth < maxDepth) {
      const sub = await buildDir(
        listDirectoryTool,
        item.path,
        depth + 1,
        maxDepth,
        maxPerDir,
        budget
      );
      children.push(sub);
    } else {
      children.push({
        type: "directory",
        name: item.name,
        path: item.path,
        maxDepthReached: true,
      });
    }
  }

  let filesAdded = 0;
  for (const item of shownFiles) {
    if (budget.remaining <= 0) {
      budget.truncatedGlobally = true;
      break;
    }
    budget.remaining -= 1;
    filesAdded += 1;
    children.push({
      type: "file",
      name: item.name,
      path: item.path,
    });
  }

  const filesDroppedAfterShown = shownFiles.length - filesAdded;
  if (filesDroppedAfterShown > 0) {
    const extraNote = `${filesDroppedAfterShown} file${
      filesDroppedAfterShown === 1 ? "" : "s"
    } not listed (overview node budget exhausted).`;
    truncation = {
      ...(truncation || {}),
      filesNotListedDueToOverviewBudget: filesDroppedAfterShown,
      note: truncation?.note
        ? `${truncation.note} ${extraNote}`
        : extraNote,
    };
  }

  const out = {
    type: "directory",
    path,
    name: displayName,
    children,
  };

  const hasTrunc =
    truncation &&
    (truncation.moreFolders > 0 ||
      truncation.moreFiles > 0 ||
      (truncation.filesNotListedDueToOverviewBudget || 0) > 0);

  if (hasTrunc) {
    out.truncation = truncation;
  }

  return out;
}
